import { is_prod } from "../../../globals/utils/constants";
import {
  parseCurrencyToNumber,
  safeNumber,
} from "./formHelpers";
import { getUnderwritingThreshold } from "./validateStepHelpers";
import {
  getBondPayloadKindConfig,
  getRetrievePrincipalCount,
} from "./bondPayloadConfig";
import { normalizeGuardiansForPayload } from "./guardianCitizenshipHelpers";

export const BOND_PAYLOAD_MODE = {
  CREATE: "create",
  RETRIEVE: "retrieve",
  HISTORIC: "historic",
};

const yesNo = (value) => value === "Yes";

export function resolveEstimatedEstateAssets(estAssets, mode) {
  if (estAssets === "") {
    return null;
  }
  if (mode === BOND_PAYLOAD_MODE.RETRIEVE) {
    return estAssets ?? null;
  }
  if (estAssets == null) {
    return null;
  }
  return Number.parseFloat(estAssets).toFixed(2);
}

const parseIndemnityReceived = (value) =>
  value === "" ? null : value === "Yes";

function buildPersonLegalName(basicInformation) {
  return [
    basicInformation?.firstName,
    basicInformation?.middleName,
    basicInformation?.lastName,
    basicInformation?.suffix,
  ]
    .filter(Boolean)
    .join(" ");
}

function buildGuestReferralSpread(isGuestRequest) {
  if (!isGuestRequest) return {};
  try {
    const r = JSON.parse(localStorage.getItem("guestReferralData"));
    return r
      ? {
          referral_source: r.referral_source || null,
          referred_by: r.referred_by || null,
        }
      : {};
  } catch {
    return {};
  }
}

function buildBillingFields(billing) {
  return {
    billing_entity: billing?.billing_entity,
    billing_lawfirmaddress_name: billing?.billing_lawfirmaddress_name,
    billing_principal_name: billing?.billing_principal_name,
    billing_other_name: billing?.billing_other_name,
    billing_other_address: billing?.billing_other_address,
    billing_other_relationship: billing?.billing_other_relationship,
    billing_other_city: billing?.billing_other_city || "",
    billing_other_state: billing?.billing_other_state || "",
    billing_other_zip: billing?.billing_other_zip || "",
  };
}

function buildCommonDocumentFields(docs) {
  return {
    copy_of_court_order: docs?.courtOrderFileName,
    copy_of_court_order_link: docs?.courtOrderLink,
    other_document: docs?.otherDocumentFileName,
    other_document_link: docs?.otherDocumentLink,
  };
}

function buildRetrieveDocumentFields(
  docs,
  { billing, billingMethod, isIndemnityReceived, general }
) {
  return {
    notes: docs?.note,
    cancellation_document: docs?.cancellationDocumentFileName,
    cancellation_document_link: docs?.cancellationDocumentLink,
    endorsement_document: docs?.endorsementDocumentFileName,
    endorsement_document_link: docs?.endorsementDocumentLink,
    endorsement_poa_document: docs?.endorsementPOADocumentFileName,
    endorsement_poa_document_link: docs?.endorsementPOADocumentLink,
    credit_report_document: docs?.creditDocumentFileName,
    credit_report_document_link: docs?.creditDocumentLink,
    fcra_document: docs?.fcraDocumentFileName,
    fcra_document_link: docs?.fcraDocumentLink,
    billing_method: billing?.billing_method || billingMethod || "",
    other_custno: billing?.custNo,
    credit_report_allowed: yesNo(general?.requestForAuthorization),
    is_indemnity_received: parseIndemnityReceived(isIndemnityReceived),
  };
}

function buildRetrieveEnvelope(globalState, kind, { status, billingMethod }) {
  const kindConfig = getBondPayloadKindConfig(kind);
  const basic = globalState.basicInformation;
  const name =
    kindConfig.nameStrategy === "legalName"
      ? basic?.legalName
      : `${basic?.firstName} ${basic?.lastName}`;

  return {
    bond_type: kindConfig.bondType,
    bond_sub_category: kindConfig.getBondSubCategory(globalState),
    bond_amount: basic?.bondAmount,
    bondNumber: globalState.bondNumber?.bond_number?.bondno_id ?? null,
    premium: basic?.premiumAmount,
    court_case_number: basic?.courtCaseNumber,
    state: 6,
    bond_carrier: is_prod ? 7 : 4,
    county: basic?.county,
    is_esignatures: basic?.is_esignatures === "Yes",
    status,
    updated_at: new Date().toISOString(),
    law_firm: globalState.attorneyInformation?.lawFirm,
    law_firm_location: globalState.attorneyInformation?.lawFirmAddress,
    law_firm_user: globalState.attorneyInformation?.attorneyOfRecord,
    obligee: globalState.obligeeInformation?.obligeeId,
    requester_user_email: globalState.requestorInformation?.email,
    requester_phone_number: globalState.requestorInformation?.phoneNumber,
    billing_method: billingMethod || "",
    name,
    requester_name: globalState.requestorInformation?.firstName,
  };
}

function buildCreateAttorneyFields(state, isGuestRequest) {
  return {
    law_firm: isGuestRequest ? null : state.attorneyInformation?.lawFirm,
    law_firm_location: isGuestRequest
      ? null
      : state.attorneyInformation?.lawFirmAddress,
    law_firm_user: isGuestRequest
      ? null
      : state.attorneyInformation?.attorneyOfRecord,
    guest_lawfirm_name: isGuestRequest
      ? state.attorneyInformation?.guest_lawfirm_name
      : undefined,
    guest_lawfirmaddress_name: isGuestRequest
      ? state.attorneyInformation?.guest_lawfirmaddress_name
      : undefined,
    guest_attorney_name: isGuestRequest
      ? state.attorneyInformation?.guest_attorney_name
      : undefined,
  };
}

function buildCreateCommonEnvelope(state, context) {
  const { isGuestRequest, userdetails, lawfirmdetails } = context;
  const now = new Date().toISOString();

  return {
    bond_amount: safeNumber(
      parseCurrencyToNumber(state.basicInformation?.bondAmount)
    ),
    premium: state.basicInformation?.premiumAmount,
    court_case_number:
      state.basicInformation?.hasCourtCaseNumber === "Yes"
        ? state.basicInformation?.courtCaseNumber
        : "",
    state: 6,
    bond_carrier: is_prod ? 7 : 4,
    county: state.basicInformation?.county,
    is_guest: isGuestRequest,
    ...buildGuestReferralSpread(isGuestRequest),
    status: "Active",
    created_at: now,
    updated_at: now,
    ...buildCreateAttorneyFields(state, isGuestRequest),
    obligee: state.obligeeInformation?.obligeeId,
    requester_id: isGuestRequest ? null : userdetails.id,
    managing_lawfirm:
      lawfirmdetails.lawfirmId || state.attorneyInformation?.lawFirm,
    requester_user_email: state.requestorInformation?.email,
    requester_phone_number: state.requestorInformation?.phoneNumber,
    name:
      state.basicInformation?.firstName +
      " " +
      state.basicInformation?.lastName,
    requester_name:
      state.requestorInformation?.firstNameReq +
      " " +
      state.requestorInformation?.lastNameReq,
  };
}

function buildGuardianshipBondNested(state, mode, { billingMethod, isIndemnityReceived } = {}) {
  const basic = state.basicInformation;
  const general = state.generalInformation;
  const docs = state.documentUpload;
  const billing = state.billingInformation;
  const explanations = general?.explanations || {};

  const nested = {
    ward_legal_name: buildPersonLegalName(basic),
    ward_date_of_birth: basic?.dateOfDeath,
    number_of_guardians: basic?.GuardianNumber,
    guardians_all_us_canadian_uk_citizens: true,
    fiduciary_operate_wards_business: yesNo(general?.ongoingBusiness),
    fiduciary_business_explanation: explanations.ongoingBusiness,
    guardian_indebited_to_estate: yesNo(general?.indebtedGuardians),
    guardian_indebted_explanation: explanations.indebtedGuardians,
    ward_execute_last_will_and_testament: yesNo(general?.didLastWillAndTestament),
    copy_of_last_will_and_testament: docs?.lastWillFileName,
    copy_of_last_will_and_testament_link: docs?.lastWillLink,
    guardian_replace_prior_fiduciary: yesNo(general?.replacingFiduciary),
    bond_replace_prior_bond: yesNo(general?.replacingPriorBond),
    replaced_by_non_court: yesNo(general?.demandedByInterestedParty),
    replaced_non_court_explanation: explanations.demandedByInterestedParty,
    joint_control_exercised: yesNo(general?.jointControlExercised),
    supplemental_needs_trust_created: yesNo(general?.supplementalNeedsTrust),
    copy_of_supplemental_needs_trust: docs?.otherDocumentFileName,
    copy_of_supplemental_needs_trust_link: docs?.otherDocumentLink,
    ward_first_name: basic?.firstName,
    ward_middle_name: basic?.middleName,
    ward_last_name: basic?.lastName,
    ward_suffix: basic?.suffix,
    ward_alias: basic?.aliases,
    ward_execute_last_will_and_testament_explaination:
      explanations.didLastWillAndTestament,
    guardian_replace_prior_fiduciary_explaination:
      explanations.replacingFiduciary,
    bond_replace_prior_bond_explaination: explanations.replacingPriorBond,
    required_non_court_explanation: explanations.demandedByInterestedParty,
    joint_control_explanation: explanations.jointControlExercised,
    supplemental_needs_trust_explanation: explanations.supplementalNeedsTrust,
    is_bankrupt_insolvent: yesNo(general?.bankruptOrInsolvent),
    is_bankrupt_insolvent_explanation: explanations.bankruptOrInsolventExplanation,
    is_lawsuits_judgements_liens: yesNo(general?.outstandingLawsuit),
    is_lawsuits_judgements_liens_explanation:
      explanations.outstandingLawsuitExplanation,
    copy_of_court_order: docs?.courtOrderFileName,
    copy_of_court_order_link: docs?.courtOrderLink,
    other_document: docs?.otherDocumentFileName,
    other_document_link: docs?.otherDocumentLink,
    ...buildBillingFields(billing),
    is_declined: yesNo(general?.isDeclined),
    is_declined_explanation: explanations.isDeclined,
  };

  if (mode === BOND_PAYLOAD_MODE.RETRIEVE) {
    return {
      ...nested,
      ...buildRetrieveDocumentFields(docs, {
        billing,
        billingMethod,
        isIndemnityReceived,
        general,
      }),
    };
  }

  if (mode === BOND_PAYLOAD_MODE.CREATE) {
    return {
      ...nested,
      notes: docs?.note?.substring(0, 499),
      is_billing_override: billing?.is_billing_override ?? false,
    };
  }

  return {
    ...nested,
    notes: docs?.note,
    credit_report_allowed: yesNo(general?.requestForAuthorization),
  };
}

function buildVaBondNested(state, mode, { billingMethod, isIndemnityReceived } = {}) {
  const basic = state.basicInformation;
  const general = state.generalInformation;
  const docs = state.documentUpload;
  const billing = state.billingInformation;
  const explanations = general?.explanations || {};

  const nested = {
    beneficiary_legal_name: buildPersonLegalName(basic),
    beneficiary_date_of_birth: basic?.dateOfBirth,
    number_of_principals: basic?.PrincipalNumber,
    principals_all_us_canadian_uk_citizens: true,
    fiduciary_operate_beneficiarys_business: yesNo(general?.ongoingBusiness),
    fiduciary_business_explanation: explanations.ongoingBusiness,
    principal_indebted_to_estate: yesNo(general?.indebtedGuardians),
    principal_indebted_explanation: explanations.indebtedGuardians,
    beneficiary_execute_last_will_and_testament: yesNo(
      general?.didLastWillAndTestament
    ),
    copy_of_last_will_and_testament: docs?.lastWillFileName,
    copy_of_last_will_and_testament_link: docs?.lastWillLink,
    principal_replace_prior_fiduciary: yesNo(general?.replacingFiduciary),
    bond_replace_prior_bond: yesNo(general?.replacingPriorBond),
    replaced_by_non_court: yesNo(general?.demandedByInterestedParty),
    replaced_non_court_explanation: explanations.demandedByInterestedParty,
    joint_control_exercised: yesNo(general?.jointControlExercised),
    supplemental_needs_trust_created: yesNo(general?.supplementalNeedsTrust),
    copy_of_supplemental_needs_trust: docs?.otherDocumentFileName,
    copy_of_supplemental_needs_trust_link: docs?.otherDocumentLink,
    beneficiary_first_name: basic?.firstName,
    beneficiary_middle_name: basic?.middleName,
    beneficiary_last_name: basic?.lastName,
    beneficiary_suffix: basic?.suffix,
    beneficiary_alias: basic?.aliases,
    beneficiary_execute_last_will_and_testament_explaination:
      explanations.didLastWillAndTestament,
    principal_replace_prior_fiduciary_explaination:
      explanations.replacingFiduciary,
    bond_replace_prior_bond_explaination: explanations.replacingPriorBond,
    required_non_court_explanation: explanations.demandedByInterestedParty,
    joint_control_explanation: explanations.jointControlExercised,
    supplemental_needs_trust_explanation: explanations.supplementalNeedsTrust,
    is_bankrupt_insolvent: yesNo(general?.bankruptOrInsolvent),
    is_bankrupt_insolvent_explanation: explanations.bankruptOrInsolventExplanation,
    is_lawsuits_judgements_liens: yesNo(general?.outstandingLawsuit),
    is_lawsuits_judgements_liens_explanation:
      explanations.outstandingLawsuitExplanation,
    copy_of_court_order: docs?.courtOrderFileName,
    copy_of_court_order_link: docs?.courtOrderLink,
    other_document: docs?.otherDocumentFileName,
    other_document_link: docs?.otherDocumentLink,
    ...buildBillingFields(billing),
    is_declined: yesNo(general?.isDeclined),
    is_declined_explanation: explanations.isDeclined,
  };

  if (mode === BOND_PAYLOAD_MODE.RETRIEVE) {
    return {
      ...nested,
      ...buildRetrieveDocumentFields(docs, {
        billing,
        billingMethod,
        isIndemnityReceived,
        general,
      }),
    };
  }

  if (mode === BOND_PAYLOAD_MODE.CREATE) {
    return {
      ...nested,
      notes: docs?.note?.substring(0, 499),
      is_billing_override: billing?.is_billing_override ?? false,
      credit_report_allowed: yesNo(general?.requestForAuthorization),
    };
  }

  return {
    ...nested,
    notes: docs?.note,
    credit_report_allowed: yesNo(general?.requestForAuthorization),
  };
}

function mapCreateGuardians(state) {
  const bondAmt = safeNumber(
    parseCurrencyToNumber(state.basicInformation?.bondAmount)
  );
  const n = Number(state.basicInformation?.GuardianNumber || 1);
  const raw = (state.guardianDetails?.guardians || []).slice(0, n);
  const underwritingThreshold = getUnderwritingThreshold(
    state.basicInformation?.bondSubCategory
  );

  return normalizeGuardiansForPayload(
    raw.map((g) => {
      const skipEsign =
        (g.is_professional_guardian === "Yes" && g.pg_gia_on_file) ||
        (g.is_attorney === "Yes" && g.attorney_gia_on_file);
      const skipCredit =
        bondAmt < underwritingThreshold ||
        g.is_attorney === "Yes" ||
        (g.is_professional_guardian === "Yes" && g.pg_loa_on_file);
      const {
        pg_gia_on_file,
        pg_loa_on_file,
        attorney_gia_on_file,
        is_esignatures_principal,
        credit_report_principal,
        ...rest
      } = g;
      return {
        ...rest,
        is_esignatures: skipEsign ? false : is_esignatures_principal === "Yes",
        credit_report_allowed: skipCredit ? null : credit_report_principal === "Yes",
      };
    })
  );
}

function sliceRetrievePrincipalGuardians(globalState, kind) {
  const guardians = globalState.guardianDetails?.guardians || [];
  const count = getRetrievePrincipalCount(globalState.basicInformation, kind);
  return guardians.slice(0, count);
}

export function buildRetrieveVaPayload(globalState, billingMethod, isIndemnityReceived, status = "Active") {
  const kindConfig = getBondPayloadKindConfig("va");
  return {
    ...buildRetrieveEnvelope(globalState, "va", { status, billingMethod }),
    [kindConfig.nestedKey]: buildVaBondNested(globalState, BOND_PAYLOAD_MODE.RETRIEVE, {
      billingMethod,
      isIndemnityReceived,
    }),
    [kindConfig.principalsKey]: sliceRetrievePrincipalGuardians(globalState, "va"),
  };
}

export function buildRetrieveTrusteePayload(globalState, status, billingMethod, isIndemnityReceived) {
  const kindConfig = getBondPayloadKindConfig("trustee");
  return {
    ...buildRetrieveEnvelope(globalState, "trustee", { status, billingMethod }),
    [kindConfig.nestedKey]: buildTrusteeBondNested(globalState, BOND_PAYLOAD_MODE.RETRIEVE, {
      billingMethod,
      isIndemnityReceived,
    }),
    [kindConfig.principalsKey]: sliceRetrievePrincipalGuardians(globalState, "trustee"),
  };
}

export function buildRetrieveGuardianshipPayload(globalState, billingMethod, isIndemnityReceived, status = "Active") {
  const kindConfig = getBondPayloadKindConfig("guardianship");
  return {
    ...buildRetrieveEnvelope(globalState, "guardianship", { status, billingMethod }),
    [kindConfig.nestedKey]: buildGuardianshipBondNested(
      globalState,
      BOND_PAYLOAD_MODE.RETRIEVE,
      { billingMethod, isIndemnityReceived }
    ),
    [kindConfig.principalsKey]: normalizeGuardiansForPayload(
      sliceRetrievePrincipalGuardians(globalState, "guardianship")
    ),
  };
}

export function buildRetrieveProbatePayload(globalState, status, billingMethod, isIndemnityReceived) {
  const kindConfig = getBondPayloadKindConfig("probate");
  return {
    ...buildRetrieveEnvelope(globalState, "probate", { status, billingMethod }),
    [kindConfig.nestedKey]: buildProbateBondNested(globalState, BOND_PAYLOAD_MODE.RETRIEVE, {
      billingMethod,
      isIndemnityReceived,
    }),
    [kindConfig.principalsKey]: sliceRetrievePrincipalGuardians(globalState, "probate"),
  };
}

export function buildCreateGuardianshipPayload(globalState, context) {
  return {
    ...buildCreateCommonEnvelope(globalState, context),
    bond_type: 3,
    bond_sub_category: globalState.basicInformation?.bondSubCategory,
    is_esignatures: false,
    guardianshipbond: buildGuardianshipBondNested(
      globalState,
      BOND_PAYLOAD_MODE.CREATE
    ),
    guardians: mapCreateGuardians(globalState),
  };
}

export function buildCreateVaPayload(globalState, context) {
  return {
    ...buildCreateCommonEnvelope(globalState, context),
    bond_type: 6,
    bond_sub_category: "None",
    is_esignatures: globalState.basicInformation?.is_esignatures === "Yes",
    vabond: buildVaBondNested(globalState, BOND_PAYLOAD_MODE.CREATE),
    principals: (globalState.guardianDetails?.guardians || []).slice(
      0,
      globalState.basicInformation?.PrincipalNumber
    ),
  };
}

function buildProbateBondNested(state, mode, { billingMethod, isIndemnityReceived } = {}) {
  const basic = state.basicInformation;
  const general = state.generalInformation;
  const docs = state.documentUpload;
  const billing = state.billingInformation;
  const explanations = general?.explanations || {};
  const bondAmount = safeNumber(parseCurrencyToNumber(basic?.bondAmount));

  const nested = {
    deceased_legal_name: buildPersonLegalName(basic),
    date_of_death: basic?.dateOfDeath,
    number_of_personal_representative: basic?.PrincipalNumber,
    us_citizen: true,
    estate_include_ongoing_business: yesNo(general?.ongoingBusiness),
    estate_include_ongoing_business_explanation: explanations.ongoingBusiness,
    personal_rep_indebted: yesNo(general?.indebtedGuardians),
    execute_last_will_testament: yesNo(general?.didLastWillAndTestament),
    copy_of_last_will_and_testament: docs?.lastWillFileName,
    copy_of_last_will_and_testament_link: docs?.lastWillLink,
    replace_prior_fiduciary: yesNo(general?.replacingFiduciary),
    replace_prior_fiduciary_explanation: explanations.replacingFiduciary,
    replace_prior_bond: yesNo(general?.replacingPriorBond),
    demand_other_than_court: yesNo(general?.demandedByInterestedParty),
    demand_other_than_court_explanation: explanations.demandedByInterestedParty,
    estimated_estate_assets: resolveEstimatedEstateAssets(
      general?.estAssets,
      mode,
    ),
    deceased_legal_first_name: basic?.firstName,
    deceased_legal_middle_name: basic?.middleName,
    deceased_legal_last_name: basic?.lastName,
    deceased_legal_suffix: basic?.suffix,
    alias_deceased_legal_name: basic?.aliases,
    ever_bankrupt_insolvent: yesNo(general?.bankruptOrInsolvent),
    outstanding_lawsuits: yesNo(general?.outstandingLawsuit),
    any_disputes: yesNo(general?.anyDisputes),
    personal_rep_indebted_explanation: explanations.indebtedGuardians,
    declined_by_any_other_surety_explanation: explanations.isDeclined,
    outstanding_lawsuits_explanation: explanations.outstandingLawsuitExplanation,
    ever_bankrupt_insolvent_explanation: explanations.bankruptOrInsolventExplanation,
    any_disputes_explanation: explanations.anyDisputes,
    copy_of_court_order: docs?.courtOrderFileName,
    copy_of_court_order_link: docs?.courtOrderLink,
    other_document: docs?.otherDocumentFileName,
    other_document_link: docs?.otherDocumentLink,
    ...buildBillingFields(billing),
    credit_report_allowed: yesNo(general?.requestForAuthorization),
    declined_by_any_other_surety: yesNo(general?.isDeclined),
    is_judge_stearn: yesNo(basic?.judgeStern),
    is_wrongful_death:
      basic?.wrongfulDeathLawsuit === "Yes" || basic?.wrongfulDeathLawsuit === null,
  };

  if (mode === BOND_PAYLOAD_MODE.RETRIEVE) {
    return {
      ...nested,
      ...buildRetrieveDocumentFields(docs, {
        billing,
        billingMethod,
        isIndemnityReceived,
        general,
      }),
    };
  }

  if (mode === BOND_PAYLOAD_MODE.CREATE) {
    return {
      ...nested,
      notes: docs?.note?.substring(0, 499),
      is_billing_override: billing?.is_billing_override ?? false,
      billing_method: bondAmount <= 18000 ? "Direct Bill" : null,
    };
  }

  return nested;
}

function buildTrusteeBondNested(state, mode, { billingMethod, isIndemnityReceived } = {}) {
  const basic = state.basicInformation;
  const general = state.generalInformation;
  const docs = state.documentUpload;
  const billing = state.billingInformation;
  const explanations = general?.explanations || {};

  const nested = {
    county: basic?.county,
    amount: basic?.bondAmount,
    court_file_no:
      basic?.hasCourtCaseNumber === "Yes" ? basic?.courtCaseNumber : "",
    legal_name: basic?.legalName,
    trust_name: basic?.trustName,
    dob: basic?.dateOfBirth,
    number_of_principals: basic?.PrincipalNumber,
    all_us_canadian_uk_citizens: yesNo(general?.allUSUKCanada),
    all_us_canadian_uk_citizens_explanation: "",
    any_business_operated: yesNo(general?.ongoingBusiness),
    any_business_operated_explanation: explanations.ongoingBusiness,
    indebted_state: yesNo(general?.indebtedGuardians),
    indebted_state_explanation: explanations.indebtedGuardians,
    is_replaced_prior: yesNo(general?.replacingPriorBond),
    is_replaced_prior_explanation: explanations.replacingPriorBond,
    is_supplemental_needs: yesNo(general?.supplementalNeedsTrust),
    copy_of_court_order: docs?.courtOrderFileName,
    copy_of_court_order_link: docs?.courtOrderLink,
    other_document: docs?.otherDocumentFileName,
    other_document_link: docs?.otherDocumentLink,
    ...buildBillingFields(billing),
    credit_report_allowed: yesNo(general?.requestForAuthorization),
  };

  if (mode === BOND_PAYLOAD_MODE.RETRIEVE) {
    return {
      ...nested,
      ...buildRetrieveDocumentFields(docs, {
        billing,
        billingMethod,
        isIndemnityReceived,
        general,
      }),
    };
  }

  if (mode === BOND_PAYLOAD_MODE.CREATE) {
    return {
      ...nested,
      is_billing_override: billing?.is_billing_override ?? false,
    };
  }

  return nested;
}

export function buildCreateProbatePayload(globalState, context) {
  return {
    ...buildCreateCommonEnvelope(globalState, context),
    bond_type: 1,
    bond_sub_category: globalState.basicInformation?.bondSubCategory,
    is_esignatures: globalState.basicInformation?.is_esignatures === "Yes",
    probatebond: buildProbateBondNested(globalState, BOND_PAYLOAD_MODE.CREATE),
    personal_rep: (globalState.guardianDetails?.guardians || []).slice(
      0,
      globalState.basicInformation?.PrincipalNumber
    ),
  };
}

export function buildCreateTrusteePayload(globalState, context) {
  const requestor = globalState.requestorInformation || {};
  return {
    ...buildCreateCommonEnvelope(globalState, context),
    bond_type: 4,
    bond_sub_category: globalState.basicInformation?.bondSubCategory,
    is_esignatures: globalState.basicInformation?.is_esignatures === "Yes",
    name: globalState.basicInformation?.legalName,
    requester_name:
      `${requestor.firstNameReq || ""} ${requestor.lastNameReq || ""}`.trim(),
    trustee_bond: buildTrusteeBondNested(globalState, BOND_PAYLOAD_MODE.CREATE),
    trustees: (globalState.guardianDetails?.guardians || []).slice(
      0,
      globalState.basicInformation?.PrincipalNumber
    ),
  };
}

const RETRIEVE_BUILDERS = {
  va: buildRetrieveVaPayload,
  guardianship: buildRetrieveGuardianshipPayload,
  trustee: buildRetrieveTrusteePayload,
  probate: buildRetrieveProbatePayload,
};

const CREATE_BUILDERS = {
  guardianship: buildCreateGuardianshipPayload,
  va: buildCreateVaPayload,
  probate: buildCreateProbatePayload,
  trustee: buildCreateTrusteePayload,
};

export function buildBondPayload(bondKind, mode, globalState, options = {}) {
  if (mode === BOND_PAYLOAD_MODE.RETRIEVE) {
    const builder = RETRIEVE_BUILDERS[bondKind];
    if (!builder) {
      throw new Error(`Retrieve payload not supported for bond kind: ${bondKind}`);
    }
    const {
      billingMethod,
      isIndemnityReceived,
      status = "Active",
    } = options;
    if (bondKind === "trustee" || bondKind === "probate") {
      return builder(globalState, status, billingMethod, isIndemnityReceived);
    }
    return builder(globalState, billingMethod, isIndemnityReceived, status);
  }

  if (mode === BOND_PAYLOAD_MODE.CREATE) {
    const builder = CREATE_BUILDERS[bondKind];
    if (!builder) {
      throw new Error(`Create payload not supported for bond kind: ${bondKind}`);
    }
    return builder(globalState, options);
  }

  if (mode === BOND_PAYLOAD_MODE.HISTORIC) {
    throw new Error(
      `Historic payloads for ${bondKind} remain in historicBondCreateConfig`
    );
  }

  throw new Error(`Unknown bond payload mode: ${mode}`);
}

/** @param {object} basePayload @param {object} representative */
export function buildProbateSplitPayload(basePayload, representative) {
  return {
    ...basePayload,
    probatebond: {
      ...basePayload.probatebond,
      number_of_personal_representative: 1,
    },
    personal_rep: [representative],
  };
}

/**
 * @param {import("./bondFormConfig").BondFormKindConfig} config
 * @param {object} globalState
 * @param {object} context
 */
export function buildBondFormCreatePayload(config, globalState, context) {
  return buildBondPayload(config.payloadKind, BOND_PAYLOAD_MODE.CREATE, globalState, context);
}
